import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Header } from '../components/Header';
import { GoogleLogo, EnvelopeSimple, PawPrint, ArrowLeft, ShieldCheck, Lock, Eye, EyeSlash } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LoginPage() {
  const { login, loginAdmin, isAuthenticated, user, sendEmailCode, verifyEmailCode } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Detect admin mode: /login?admin=1
  const isAdminMode = searchParams.get('admin') === '1';

  // Common
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP flow (normal users)
  const [step, setStep] = useState('email');
  const [otp, setOtp] = useState('');

  // Admin password flow
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [adminMode, setAdminMode] = useState(isAdminMode);

  if (isAuthenticated) {
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (!user?.role) return <Navigate to="/onboarding" replace />;
    return <Navigate to={user.role === 'prestador' ? '/provider/dashboard' : '/client/home'} replace />;
  }

  // ── Admin login ──────────────────────────────────────────
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error('Preencha email e password.');
      return;
    }
    setLoading(true);
    try {
      await loginAdmin(email.trim(), password);
      toast.success('Bem-vindo ao painel de administração!');
    } catch (error) {
      toast.error(error.message || 'Credenciais inválidas.');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP flow ─────────────────────────────────────────────
  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Por favor introduza o seu email.'); return; }
    setLoading(true);
    try {
      await sendEmailCode(email.trim());
      toast.success('Código enviado para o seu email!');
      setStep('otp');
    } catch (error) {
      toast.error(error.message || 'Não foi possível enviar o email. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    if (otp.length !== 4) { toast.error('Introduza o código de 4 dígitos.'); return; }
    setLoading(true);
    try {
      await verifyEmailCode(email.trim(), otp);
      toast.success('Login efetuado com sucesso!');
    } catch (error) {
      toast.error(error.message || 'Código inválido ou expirado.');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await sendEmailCode(email.trim());
      toast.success('Novo código enviado!');
      setOtp('');
    } catch (error) {
      toast.error('Não conseguimos enviar o email neste momento.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpComplete = (value) => {
    setOtp(value);
    if (value.length === 4) setTimeout(() => handleVerifyCode(), 200);
  };

  const maskedEmail = email ? email.replace(/(.{2})(.*)(@)/, '$1***$3') : '';

  // ── Admin Login Form ──────────────────────────────────────
  if (adminMode) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-4">
                <ShieldCheck weight="fill" className="w-8 h-8 text-primary-foreground" />
              </div>
              <h1 className="text-3xl font-bold font-outfit">Acesso Administrativo</h1>
              <p className="text-muted-foreground mt-2">Painel de administração do PasseioPerfeito</p>
            </div>

            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary" />
                  Login de Administrador
                </CardTitle>
                <CardDescription>Introduza as suas credenciais de administrador</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAdminLogin} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Email</label>
                    <Input
                      type="email"
                      placeholder="admin@passeioperfeito.pt"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12"
                      required
                      autoComplete="email"
                      data-testid="admin-email-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Password</label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12 pr-12"
                        required
                        autoComplete="current-password"
                        data-testid="admin-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-full text-base"
                    disabled={loading}
                    data-testid="admin-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        A verificar...
                      </span>
                    ) : (
                      <><ShieldCheck className="w-5 h-5 mr-2" />Entrar como Administrador</>
                    )}
                  </Button>
                </form>

                <div className="mt-6 pt-4 border-t text-center">
                  <button
                    onClick={() => setAdminMode(false)}
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mx-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Voltar ao login normal
                  </button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // ── Normal Login Form ─────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-4">
              <PawPrint weight="fill" className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold font-outfit">Bem-vindo ao PasseioPerfeito</h1>
            <p className="text-muted-foreground mt-2">Entre para encontrar o melhor cuidado para o seu pet</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Entrar ou Registar</CardTitle>
              <CardDescription>
                {step === 'email' ? 'Escolha como pretende entrar' : `Código enviado para ${maskedEmail}`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <AnimatePresence mode="wait">
                {step === 'email' ? (
                  <motion.div key="email-step" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-4">
                    <Button onClick={login} className="w-full h-12 rounded-full text-base" data-testid="google-login-btn">
                      <GoogleLogo weight="bold" className="w-5 h-5 mr-2" />
                      Continuar com Google
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">ou</span>
                      </div>
                    </div>

                    <form onSubmit={handleSendCode} className="space-y-3">
                      <Input
                        type="email"
                        placeholder="O seu email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12"
                        required
                        data-testid="email-input"
                      />
                      <Button type="submit" variant="outline" className="w-full h-12 rounded-full text-base" disabled={loading} data-testid="send-code-btn">
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />A enviar...
                          </span>
                        ) : (
                          <><EnvelopeSimple weight="bold" className="w-5 h-5 mr-2" />Enviar código por email</>
                        )}
                      </Button>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div key="otp-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                    <button onClick={() => { setStep('email'); setOtp(''); }} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-email-btn">
                      <ArrowLeft className="w-4 h-4" />Alterar email
                    </button>
                    <div className="text-center space-y-3">
                      <p className="text-sm text-muted-foreground">Introduza o código de 4 dígitos enviado para</p>
                      <p className="font-medium">{maskedEmail}</p>
                    </div>
                    <form onSubmit={handleVerifyCode} className="space-y-5">
                      <div className="flex justify-center">
                        <InputOTP maxLength={4} value={otp} onChange={handleOtpComplete} data-testid="otp-input">
                          <InputOTPGroup>
                            <InputOTPSlot index={0} className="w-14 h-14 text-xl" />
                            <InputOTPSlot index={1} className="w-14 h-14 text-xl" />
                            <InputOTPSlot index={2} className="w-14 h-14 text-xl" />
                            <InputOTPSlot index={3} className="w-14 h-14 text-xl" />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      <p className="text-xs text-center text-muted-foreground">O código expira em 10 minutos.</p>
                      <Button type="submit" className="w-full h-12 rounded-full text-base" disabled={loading || otp.length !== 4} data-testid="verify-code-btn">
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />A verificar...
                          </span>
                        ) : 'Verificar e Entrar'}
                      </Button>
                      <div className="text-center">
                        <button type="button" onClick={handleResendCode} disabled={loading} className="text-sm text-primary hover:underline disabled:opacity-50" data-testid="resend-code-btn">
                          Não recebeu? Reenviar código
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              <p className="text-center text-sm text-muted-foreground pt-2">
                Ao continuar, aceita os nossos{' '}
                <Link to="/termos" className="text-primary hover:underline">Termos de Serviço</Link>
                {' '}e{' '}
                <Link to="/seguranca" className="text-primary hover:underline">Política de Privacidade</Link>
              </p>

              {/* Hidden admin access link */}
              <div className="text-center pt-1">
                <button onClick={() => setAdminMode(true)} className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors">
                  Acesso Admin
                </button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
